import React, { Component } from "react";
import { SERVER_TIMEOUT } from "../constants";
import PolicyCards from "./PolicyCards";
import { PolicyType, SendWSCommand, WSCommandType } from "../types";
import "./Prompts.css";

type ChancellorLegislativePromptProps = {
  policyOptions: PolicyType[];
  sendWSCommand: SendWSCommand;
  fascistPolicies: number;
  showError: (message: string) => void;
  enableVeto: boolean;
  /** Name of the President who handed over the policies. */
  president?: string;
  /** True when the Chancellor asked for a veto and the President refused: they must now enact a policy. */
  vetoDenied?: boolean;
};

type ChancellorLegislativePromptState = {
  selection: number | undefined;
  waitingForServer: boolean;
};

class ChancellorLegislativePrompt extends Component<
  ChancellorLegislativePromptProps,
  ChancellorLegislativePromptState
> {
  timeoutID: NodeJS.Timeout | undefined;

  constructor(props: ChancellorLegislativePromptProps) {
    super(props);
    this.state = {
      selection: undefined,
      waitingForServer: false,
    };
    this.onEnactButtonClick = this.onEnactButtonClick.bind(this);
    this.onVetoButtonClick = this.onVetoButtonClick.bind(this);
  }

  lock() {
    // Lock the buttons so that they can't be pressed multiple times.
    this.setState({ waitingForServer: true });
    this.timeoutID = setTimeout(() => {
      this.setState({ waitingForServer: false });
    }, SERVER_TIMEOUT);
  }

  onEnactButtonClick() {
    if (this.state.selection === undefined) {
      return;
    }
    this.lock();
    this.props.sendWSCommand({
      command: WSCommandType.REGISTER_CHANCELLOR_CHOICE,
      choice: this.state.selection,
    });
  }

  onVetoButtonClick() {
    if (this.props.fascistPolicies === 5) {
      // Veto power is activated.
      this.lock();
      this.props.sendWSCommand({
        command: WSCommandType.REGISTER_CHANCELLOR_VETO,
      });
    } else {
      this.props.showError(
        "Veto power is unlocked when there are 5 fascist policies."
      );
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutID);
  }

  render() {
    return (
      <div className="pp">
        <div className="pp-kicker">Legislative session · you are Chancellor</div>
        <div className="pp-title">{this.props.vetoDenied ? "Veto denied: enact one policy" : "Enact one policy"}</div>
        <div className="pp-text">
          {this.props.president ? "President " + this.props.president + " passed you these two." : "The President passed you these two."}{" "}
          The one you don't enact is discarded.
        </div>
        {this.props.vetoDenied && (
          <div className="pp-note pp-note-danger pp-veto-denied" role="alert">
            Veto denied.{" "}
            {this.props.president ? "President " + this.props.president + " refused" : "The President refused"} your
            veto. You must enact one of these policies, and you cannot veto again this turn.
          </div>
        )}
        {this.props.fascistPolicies === 5 && !this.props.vetoDenied && (
          <div className="pp-note">
            Veto power unlocked: if you veto and the President agrees, both policies are discarded and the election
            tracker advances.
          </div>
        )}
        <PolicyCards
          policies={this.props.policyOptions}
          selection={this.state.selection}
          onSelect={(index) => this.setState({ selection: index })}
          selectedTag="Enact"
          selectedTagKind="enact"
          disabled={this.state.waitingForServer}
        />
        <div className="pp-actions">
          {this.props.enableVeto && (
            <button
              className="pp-btn pp-btn-secondary"
              onClick={this.onVetoButtonClick}
              disabled={this.state.waitingForServer}
            >
              VETO
            </button>
          )}
          <button
            id="prompt-button"
            className="gold-button pp-btn"
            onClick={this.onEnactButtonClick}
            disabled={this.state.selection === undefined || this.state.waitingForServer}
          >
            <span className="gold-label">ENACT</span>
          </button>
        </div>
      </div>
    );
  }
}

export default ChancellorLegislativePrompt;
